import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    EDIT, IDLE, PENDING, ASC, REVIEW, COMPLIANCE,
} from "const"
import { CcLibrarySet } from "server/models"
import { hasProjectAccess } from "client/utils"
import {
    PRM_CC_REVIEW,
    StoreType,
    CcReviewComplianceProps,
    CcLibrarySetList,

    LoadAllCcLibrarySets,
    LoadNestedItems,
    LoadCcReviewComplianceRevisions,
    LoadCcReviewComplianceRevisionData,
    CreateCcReviewComplianceRevision,
    SortCcLibrarySets,

    setCcReviewComplianceSetLoadState,
    setCcReviewComplianceItemLoadState,
    setCcReviewComplianceSetList,
    setCcReviewComplianceSetIds,
    setCcReviewComplianceItemList,
    setCcReviewComplianceItemGroupedIds,
    setCcReviewComplianceRevisionList,
    setCcReviewComplianceRevisionActive,
    setCcReviewComplianceFormalVisibleItemIds,
    setCcReviewComplianceVisibleItemIds,
    clearCcReviewComplianceItemGroupedIds,
} from "client/store"
import {
    Container,
    PageContent,

    Table,
    TableGroupedIds,
    OnGetDisplayedIds,

    CheckRecentVersions,
} from "client/components"

import Columns from "./table/columns"
import TableBottom from "./table/bottom"
import SetCustomView from "./table/setCustomView"
import PopupMenu from "./popupMenu"
import DisplayReviewComplianceColumns from "./displayColumns"
import Open from "./open"

const CcReviewComplianceList = ({
    setId = null,
    revisionId = null,
    complianceItemId = null,
}: CcReviewComplianceProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccReviewDocument.baseRoute)
    const activeFile = useSelector((state: StoreType) => state.ccReviewDocument.activeFile)
    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const formalTableClass = useSelector((state: StoreType) => state.ccReviewCompliance.formalTableClass)
    const reviewTableClass = useSelector((state: StoreType) => state.ccReviewCompliance.reviewTableClass)

    const setList = useSelector((state: StoreType) => state.ccReviewCompliance.setList)
    const setIds = useSelector((state: StoreType) => state.ccReviewCompliance.setIds)
    const itemList = useSelector((state: StoreType) => state.ccReviewCompliance.itemList)
    const itemGroupedIds = useSelector((state: StoreType) => state.ccReviewCompliance.itemGroupedIds)
    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewCompliance.revisionActiveList)
    const setLoadState = useSelector((state: StoreType) => state.ccReviewCompliance.setLoadState)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [isOpened, setIsOpened] = useState(false)
    const [formalGroupedIds, setFormalGroupedIds] = useState<TableGroupedIds[]>([])
    const [groupedIds, setGroupedIds] = useState<TableGroupedIds[]>([])
    const [collapsedGroupes, setCollapsedGroupes] = useState<boolean>(true) // collapse top level by default

    useEffect(() => {
        dispatch(setCcReviewComplianceSetLoadState(PENDING))
        LoadAllCcLibrarySets(userData.accountId, project.id)
        .then((res) => {
            const list: CcLibrarySetList = {}
            Object.values(res).forEach((set: CcLibrarySet) => {
                set.modules.forEach((row) => {
                    if (row.module === REVIEW) {
                        list[set.id] = set
                    }
                })
            })

            const sortedIds = SortCcLibrarySets(Object.values(list), "name", ASC, {})

            dispatch(setCcReviewComplianceSetList(list))
            dispatch(setCcReviewComplianceSetIds(sortedIds))
            dispatch(setCcReviewComplianceSetLoadState(IDLE))
        })
    }, [])

    useEffect(() => {
        if (baseRoute && activeFile && activeVersion && setId && revisionId && complianceItemId && !isOpened) {
            // open answer by url
            Open(baseRoute, COMPLIANCE, activeFile.id, activeVersion.id, setId, revisionId, complianceItemId, dispatch)
            setIsOpened(true)
        }
    }, [baseRoute, activeFile, activeVersion, isOpened])

    useEffect(() => {
        if (setLoadState === PENDING) return
        if (activeVersion) {
            dispatch(setCcReviewComplianceItemLoadState(PENDING))
            const promiseArray: Promise<any>[] = [
                // check recent versions for existing files
                CheckRecentVersions(project.id),
            ]

            Object.values(setList).forEach((set) => {
                promiseArray.push(
                    LoadNestedItems(set.id).then((res) => {
                        dispatch(setCcReviewComplianceItemList(res.itemList))
                        dispatch(setCcReviewComplianceItemGroupedIds(set.id, res.nested))
                    }),
                    LoadCcReviewComplianceRevisions(project.id, activeVersion.fileId, activeVersion.id, set.id)
                    .then((revisions) => {
                        // if revisions exist
                        if (revisions.length) {
                            const lastRevision = revisions[revisions.length - 1]
                            const activeRevision = revisionActiveList[activeVersion.id] && revisionActiveList[activeVersion.id][set.id]
                            const revision = (activeRevision && revisions.find((row) => row.id === activeRevision.id)) || lastRevision

                            dispatch(setCcReviewComplianceRevisionList(set.id, revisions))
                            dispatch(setCcReviewComplianceRevisionActive(activeVersion.id, set.id, revision))

                            return LoadCcReviewComplianceRevisionData(revision, dispatch)
                        }

                        // if no revisions - create new
                        return CreateCcReviewComplianceRevision(project.id, activeVersion.fileId, activeVersion.id, set.id)
                        .then((newRevision) => {
                            dispatch(setCcReviewComplianceRevisionList(set.id, [newRevision]))
                            dispatch(setCcReviewComplianceRevisionActive(activeVersion.id, set.id, newRevision))

                            return LoadCcReviewComplianceRevisionData(newRevision, dispatch)
                        })
                    }),
                )
            })

            Promise.all(promiseArray).then(() => {
                dispatch(setCcReviewComplianceItemLoadState(IDLE))
                setCollapsedGroupes(false) // prevent collapsing after reloading data
            })
        } else {
            dispatch(clearCcReviewComplianceItemGroupedIds())
        }
    }, [setList, activeVersion, setLoadState])

    useEffect(() => {
        // order groupedIds by setIds
        const formalGrouped: TableGroupedIds[] = []
        const grouped: TableGroupedIds[] = []
        setIds.forEach((id) => {
            const set = setList[id]
            if (itemGroupedIds[id]) {
                if (set.formal) formalGrouped.push(itemGroupedIds[id])
                else grouped.push(itemGroupedIds[id])
            }
        })
        setFormalGroupedIds(formalGrouped)
        setGroupedIds(grouped)
    }, [setIds, itemGroupedIds])

    const getFormalDisplayedIds: OnGetDisplayedIds = (itemIds: number[]) => {
        dispatch(setCcReviewComplianceFormalVisibleItemIds(itemIds))
    }

    const getDisplayedIds: OnGetDisplayedIds = (itemIds: number[]) => {
        dispatch(setCcReviewComplianceVisibleItemIds(itemIds))
    }

    const editAccess = hasProjectAccess(PRM_CC_REVIEW, EDIT)

    const formalColumns = Columns(true, editAccess)
    const reviewColumns = Columns(false, editAccess)

    return (
        <Container>
            <PageContent>
                {
                    (groupedIds.length > 0) && (
                        <Table
                            className={reviewTableClass}
                            columns={reviewColumns.columns}
                            dataList={itemList}
                            dataGroupedIds={groupedIds}
                            collapsedGroupes={collapsedGroupes}
                            onGetDisplayedIds={getDisplayedIds}
                            rowSize="fit"
                            showPagination={false}
                            showTotal={false}
                            bottomContent={editAccess && <TableBottom formal={false} />}
                            CustomViewLevel1={SetCustomView}
                        />
                    )
                }

                {
                    (formalGroupedIds.length > 0) && (
                        <Table
                            title={lang.formal}
                            className={formalTableClass}
                            columns={formalColumns.columns}
                            dataList={itemList}
                            dataGroupedIds={formalGroupedIds}
                            collapsedGroupes={collapsedGroupes}
                            onGetDisplayedIds={getFormalDisplayedIds}
                            rowSize="fit"
                            showPagination={false}
                            showTotal={false}
                            bottomContent={editAccess && <TableBottom formal />}
                            CustomViewLevel1={SetCustomView}
                        />
                    )
                }

                <PopupMenu />

                <DisplayReviewComplianceColumns
                    columns={reviewColumns.columns.filter((column) => !!column.header)} // don't show management columns
                    displayColumns={reviewColumns.displayColumns}
                    formal={false}
                />
                <DisplayReviewComplianceColumns
                    columns={formalColumns.columns.filter((column) => !!column.header)} // don't show management columns
                    displayColumns={formalColumns.displayColumns}
                    formal
                />
            </PageContent>
        </Container>
    )
}
export default CcReviewComplianceList
